import React, { useMemo, useState } from 'react';
import { IReportBuilderReport, NEW_REPORT_LABELS, ReportTypes } from 'views/ReportBuilder/constants';
import {
    Col, ListGroup, ListGroupItem, Row,
} from 'reactstrap';
import ReportDetailCard from '../components/ReportDetailCard/ReportDetailCard';
import { MAX_DISPLAYED_FILTER_VALUES } from './constants';

export interface IProps {
    report: IReportBuilderReport;
    setNewReports: React.Dispatch<React.SetStateAction<IReportBuilderReport[]>>;
}

const DocumentLibraryCard = ({ report, setNewReports }: IProps) => {
    const [showAllFolders, setShowAllFolders] = useState(false);
    const [showAllDocumentTypes, setShowAllDocumentTypes] = useState(false);
    const [showAllDocuments, setShowAllDocuments] = useState(false);

    const handleEdit = () => {
        setNewReports(prevReports => {
            const reportIndex = prevReports.findIndex(r => r.reportType === ReportTypes.DOCUMENT_LIBRARY);

            if (reportIndex === -1) {
                return prevReports;
            }

            const updatedReports = [...prevReports];
            updatedReports[reportIndex] = {
                ...prevReports[reportIndex],
                isEditing: true,
            };
            return updatedReports;
        });
    };

    const handleDelete = () => {
        setNewReports(prevReports => {
            const reportIndex = prevReports.findIndex(r => r.reportType === ReportTypes.DOCUMENT_LIBRARY);

            if (reportIndex === -1) {
                return prevReports;
            }

            const updatedReports = [...prevReports];
            updatedReports.splice(reportIndex, 1);
            return updatedReports;
        });
    };

    const displayedFolderValues = useMemo(() => {
        if (!report.selectedFolders) return [];
        return showAllFolders
            ? report.selectedFolders
            : report.selectedFolders?.slice(0, MAX_DISPLAYED_FILTER_VALUES);
    }, [report.selectedFolders, showAllFolders]);

    const displayedDocumentTypeValues = useMemo(() => {
        if (!report.selectedDocumentTypes) return [];
        return showAllDocumentTypes
            ? report.selectedDocumentTypes
            : report.selectedDocumentTypes?.slice(0, MAX_DISPLAYED_FILTER_VALUES);
    }, [report.selectedDocumentTypes, showAllDocumentTypes]);

    const displayedDocuments = useMemo(() => {
        if (!report.selectedDocuments) return [];
        return showAllDocuments
            ? report.selectedDocuments
            : report.selectedDocuments?.slice(0, MAX_DISPLAYED_FILTER_VALUES);
    }, [report.selectedDocuments, showAllDocuments]);

    let reportHeader = NEW_REPORT_LABELS.DOCUMENT_LIBRARY;
    if (report.selectedDocuments && report.selectedDocuments.length > 0)reportHeader += ` (${report.selectedDocuments.length})`;

    return (
        <ReportDetailCard
            header={reportHeader}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
        >
            <ListGroup>
                <ListGroupItem className="bg-secondary text-white">
                    <Row>
                        <Col> Filter type </Col>
                        <Col> Value </Col>
                    </Row>
                </ListGroupItem>
                {report.selectedFolders && report.selectedFolders.length > 0 && (
                    <ListGroupItem className="bg-secondary-lighten text-white">
                        <Row>
                            <Col> Folders </Col>
                            <Col>
                                {displayedFolderValues.map(folder => (
                                    <p key={`folder-value-id-${folder.value}`} className="mb-0 mt-1 text-white">{folder.label}</p>
                                ))}
                                {report.selectedFolders.length > MAX_DISPLAYED_FILTER_VALUES
                                        && (
                                            <>
                                                {!showAllFolders && <p className="mb-0">...</p>}
                                                <p
                                                    className="text-primary mb-0 cursor--pointer"
                                                    onClick={() => setShowAllFolders(!showAllFolders)}
                                                    role="presentation"
                                                >
                                                    {
                                                        showAllFolders
                                                            ? 'hide'
                                                            : `+ show ${report.selectedFolders.length - MAX_DISPLAYED_FILTER_VALUES} more`
                                                    }
                                                </p>
                                            </>
                                        )}
                            </Col>
                        </Row>
                    </ListGroupItem>
                )}
                {report.selectedDocumentTypes && report.selectedDocumentTypes.length > 0 && (
                    <ListGroupItem className="bg-secondary-lighten text-white">
                        <Row>
                            <Col> Document Types </Col>
                            <Col>
                                {displayedDocumentTypeValues.map(type => (
                                    <p key={`document-type-value-id-${type.value}`} className="mb-0 mt-1 text-white">{type.label}</p>
                                ))}
                                {report.selectedDocumentTypes.length > MAX_DISPLAYED_FILTER_VALUES
                                    && (
                                        <>
                                            {!showAllDocumentTypes && <p className="mb-0">...</p>}
                                            <p
                                                className="text-primary mb-0 cursor--pointer"
                                                onClick={() => setShowAllDocumentTypes(!showAllDocumentTypes)}
                                                role="presentation"
                                            >
                                                {
                                                    showAllDocumentTypes
                                                        ? 'hide'
                                                        : `+ show ${report.selectedDocumentTypes.length - MAX_DISPLAYED_FILTER_VALUES} more`
                                                }
                                            </p>
                                        </>
                                    )}
                            </Col>
                        </Row>
                    </ListGroupItem>
                )}
            </ListGroup>
            {report.selectedDocuments && report.selectedDocuments.length > 0 && (
                <ListGroup className="mt-2">
                    <ListGroupItem className="bg-secondary text-white">
                        {`Documents (${report.selectedDocuments.length})`}
                    </ListGroupItem>
                    {report.selectedDocuments && report.selectedDocuments.length > 0 && (
                        <>
                            {displayedDocuments.map(doc => (
                                <ListGroupItem className="bg-secondary-lighten text-white" key={`document-value-id-${doc.id}`}>
                                    <p className="mb-0 mt-1 text-white">{doc.name}</p>
                                </ListGroupItem>
                            ))}
                            {report.selectedDocuments.length > MAX_DISPLAYED_FILTER_VALUES
                                    && (
                                        <ListGroupItem className="bg-secondary-lighten text-white">
                                            <p
                                                className="text-primary mb-0 cursor--pointer"
                                                onClick={() => setShowAllDocuments(!showAllDocuments)}
                                                role="presentation"
                                            >
                                                {
                                                    showAllDocuments
                                                        ? 'hide'
                                                        : `+ show ${report.selectedDocuments.length - MAX_DISPLAYED_FILTER_VALUES} more`
                                                }
                                            </p>
                                        </ListGroupItem>
                                    )}
                        </>
                    )}
                </ListGroup>
            )}
        </ReportDetailCard>
    );
};

export default DocumentLibraryCard;
